import axiosApi from "../apis/axiosInstance";

// User Activity Log: activities only the browser sees (page views, grid exports, prints). Adds / updates / deletes
// are logged by the API itself. Logging is fire-and-forget: it never throws, waits or shows an error.
export const ActivityEvent = {
  View: "VIEW",
  Export: "EXPORT",
  Print: "PRINT",
};

const ActivityLogUrl = "/user/activity-log";
const RepeatViewMs = 2000;
// a grid export saves its file a moment later; that download is not logged a second time
const ExportDownloadMs = 15000;
let lastView = { path: "", at: 0 };
const recentExports = new Map();

const baseName = (name) =>
  String(name || "")
    .replace(/\.[^./\\]+$/, "")
    .trim()
    .toLowerCase();

/** Sends one activity; resolves to true when the API queued it. */
export const logActivity = (eventType, { description = "", data = null } = {}) => {
  if (!Number(sessionStorage.getItem("session_userid"))) return Promise.resolve(false);
  return axiosApi
    .post(ActivityLogUrl, { eventType, description, data })
    .then((res) => res?.data?.success === true)
    .catch(() => false);
};

/** One page view per navigation (StrictMode / re-renders open the same page twice in a row). */
export const logPageView = (path, now = Date.now()) => {
  if (!path || (path === lastView.path && now - lastView.at < RepeatViewMs)) return Promise.resolve(false);
  lastView = { path, at: now };
  return logActivity(ActivityEvent.View, { description: document.title || "" });
};

/** A grid export: title, file, format and number of rows. */
export const logExport = (title, { fileName = "", format = "", rows = null } = {}, now = Date.now()) => {
  if (fileName) recentExports.set(baseName(fileName), now);
  return logActivity(ActivityEvent.Export, {
    description: `Exported ${title || fileName || "grid"}${format ? ` (${format})` : ""}`,
    data: { title, fileName, format, rows },
  });
};

/** A file download that is not a grid export (custom exports, bank files, templates). */
export const logDownload = (fileName, now = Date.now()) => {
  const key = baseName(fileName);
  const exportedAt = recentExports.get(key);
  if (exportedAt && now - exportedAt < ExportDownloadMs) {
    recentExports.delete(key);
    return Promise.resolve(false);
  }
  return logActivity(ActivityEvent.Export, {
    description: `Downloaded ${fileName || "a file"}`,
    data: { fileName },
  });
};

const fileNameOf = (anchor) => {
  const name = anchor.getAttribute("download");
  if (name) return name;
  try {
    return decodeURIComponent(new URL(anchor.href).pathname.split("/").pop() || "");
  } catch {
    return "";
  }
};

const trackDownload = (anchor, now) => {
  if (!anchor || anchor.activityLogged || !anchor.hasAttribute?.("download")) return;
  anchor.activityLogged = true;
  logDownload(fileNameOf(anchor), now);
};

/**
 * Logs every file download and print of every menu: downloads go through an <a download> element
 * (file-saver, jsPDF, SheetJS and plain links all use one), prints through window.print. Installed once.
 */
export const installActivityTracking = (win = window) => {
  if (!win || win.activityTrackingInstalled) return;
  win.activityTrackingInstalled = true;

  const proto = win.HTMLAnchorElement.prototype;
  const click = proto.click;
  proto.click = function trackedClick(...args) {
    trackDownload(this);
    return click.apply(this, args);
  };
  const dispatch = proto.dispatchEvent;
  proto.dispatchEvent = function trackedDispatch(event) {
    if (event?.type === "click") trackDownload(this);
    return dispatch.call(this, event);
  };
  win.document.addEventListener(
    "click",
    (event) => trackDownload(event.target?.closest?.("a[download]")),
    true,
  );

  const print = win.print;
  win.print = function trackedPrint(...args) {
    logActivity(ActivityEvent.Print, { description: `Printed ${win.document.title || win.location.pathname}` });
    return print.apply(win, args);
  };
};

/** For tests. */
export const resetPageViewMemory = () => {
  lastView = { path: "", at: 0 };
  recentExports.clear();
};
